/** Shared UTF-8 / Base64 helpers so text tools agree on bytes, not UTF-16 units. */

export const utf8Encode = (text: string): Uint8Array => new TextEncoder().encode(text);

/** Strict by default: throws if the bytes are not valid UTF-8. */
export const utf8Decode = (bytes: Uint8Array, fatal = true): string =>
  new TextDecoder("utf-8", { fatal }).decode(bytes);

/** urlSafe gives Base64URL (- and _, no padding). */
export function bytesToBase64(bytes: Uint8Array, urlSafe = false): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  let out = btoa(bin);
  if (urlSafe) out = out.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return out;
}

/** Accepts standard or URL-safe Base64, missing padding, whitespace and a data: URL prefix. */
export function base64ToBytes(input: string): Uint8Array {
  let s = input.trim().replace(/^data:[^,]*;base64,/i, "").replace(/\s+/g, "");
  s = s.replace(/-/g, "+").replace(/_/g, "/");
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(s)) throw new Error("Not valid Base64");
  s = s.replace(/=+$/, "");
  if (s.length % 4 === 1) throw new Error("Not valid Base64");
  s += "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export const textToBase64 = (text: string, urlSafe = false) => bytesToBase64(utf8Encode(text), urlSafe);
export const base64ToText = (b64: string) => utf8Decode(base64ToBytes(b64));

/** Describes why a Base64 string is invalid, with a 1-based position in the text as typed; null when it parses. */
export function base64Problem(input: string): { position: number; message: string } | null {
  const prefix = input.match(/^\s*data:[^,]*;base64,/i);
  const start = prefix ? prefix[0].length : 0;
  let seenPad = false;
  let count = 0;
  for (let i = start; i < input.length; i++) {
    const ch = input[i];
    if (/\s/.test(ch)) continue;
    if (ch === "=") { seenPad = true; count++; continue; }
    if (seenPad || !/[A-Za-z0-9+/_-]/.test(ch)) {
      const shown = ch === "\n" ? "line break" : `"${ch}"`;
      return { position: i + 1, message: seenPad && /[A-Za-z0-9+/_-]/.test(ch) ? `Not valid Base64: data continues after the "=" padding at position ${i + 1}.` : `Not valid Base64: unexpected ${shown} at position ${i + 1}.` };
    }
    count++;
  }
  const padless = input.slice(start).replace(/[\s=]/g, "").length;
  if (padless === 0 && !input.slice(start).trim()) return { position: 1, message: "Nothing to decode. Paste a Base64 string first." };
  if (padless % 4 === 1) return { position: input.length, message: "Not valid Base64: the length is wrong (one character left over at the end)." };
  void count;
  return null;
}

export const bytesToHex = (bytes: Uint8Array): string => Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(" ");
